import React, { useState, useEffect } from 'react';
import { XMarkIcon, MapPinIcon } from '@heroicons/react/24/outline';
import AddressAutocomplete from './AddressAutocomplete';
import VenueMap from './VenueMap';
import { loadGoogleMapsAPI } from '../utils/googleMaps';

interface VenueData {
  id?: string;
  name: string;
  address: string;
  city: string;
  country: string;
  timezone: string;
  contact_email?: string;
  contact_phone?: string;
  latitude?: number;
  longitude?: number;
}

interface VenueEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: VenueData) => Promise<void>;
  venue?: VenueData | null;
  mode: 'create' | 'edit';
}

const TIMEZONES = [
  { value: 'America/Los_Angeles', label: 'Pacific Time (US)' },
  { value: 'America/Denver', label: 'Mountain Time (US)' },
  { value: 'America/Chicago', label: 'Central Time (US)' },
  { value: 'America/New_York', label: 'Eastern Time (US)' },
  { value: 'America/Toronto', label: 'Eastern Time (Canada)' },
  { value: 'America/Vancouver', label: 'Pacific Time (Canada)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam (CET/CEST)' },
  { value: 'Europe/Madrid', label: 'Madrid (CET/CEST)' },
  { value: 'Europe/Rome', label: 'Rome (CET/CEST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEDT/AEST)' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEDT/AEST)' },
  { value: 'UTC', label: 'UTC' },
];

const VenueEditModal: React.FC<VenueEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  venue,
  mode
}) => {
  const [formData, setFormData] = useState<VenueData>({
    name: '',
    address: '',
    city: '',
    country: '',
    timezone: 'America/Los_Angeles',
    contact_email: '',
    contact_phone: '',
    latitude: undefined,
    longitude: undefined
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showMap, setShowMap] = useState(false);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [useAutocomplete, setUseAutocomplete] = useState(true);

  useEffect(() => {
    if (venue) {
      setFormData({
        ...venue,
        country: venue.country || '',
        contact_email: venue.contact_email || '',
        contact_phone: venue.contact_phone || '',
        latitude: venue.latitude,
        longitude: venue.longitude
      });
      setShowMap(!!(venue.latitude && venue.longitude));
    } else {
      setFormData({
        name: '',
        address: '',
        city: '',
        country: '',
        timezone: 'America/Los_Angeles',
        contact_email: '',
        contact_phone: '',
        latitude: undefined,
        longitude: undefined
      });
      setShowMap(false);
    }
  }, [venue]);

  useEffect(() => {
    // Load Google Maps API when modal opens
    if (isOpen) {
      loadGoogleMapsAPI()
        .then(() => setMapsLoaded(true))
        .catch(err => {
          console.warn('Google Maps failed to load:', err);
          setMapsLoaded(false);
          setUseAutocomplete(false);
        });
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddressSelect = (components: any) => {
    const streetNumber = components.street_number || '';
    const route = components.route || '';
    const streetAddress = [streetNumber, route].filter(Boolean).join(' ');
    
    setFormData(prev => ({
      ...prev,
      address: streetAddress || components.formatted_address || prev.address,
      city: components.locality || prev.city,
      country: components.country || prev.country,
      latitude: components.lat,
      longitude: components.lng
    }));
    
    setShowMap(true);
  };

  const handleMapLocationChange = (lat: number, lng: number) => {
    setFormData(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await onSave(formData);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save venue');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">
            {mode === 'create' ? 'Create New Venue' : 'Edit Venue'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:text-gray-400"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-md">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Venue Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="The Music Hall"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Street Address *
              </label>
              {mapsLoaded && useAutocomplete ? (
                <AddressAutocomplete
                  value={formData.address}
                  onChange={(address) => setFormData(prev => ({ ...prev, address }))}
                  onAddressSelect={handleAddressSelect}
                  placeholder="Start typing an address..."
                  required
                />
              ) : (
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="123 Main Street, Suite 100"
                  required
                />
              )}
              {!mapsLoaded && useAutocomplete && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Loading address autocomplete...</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                City *
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="San Francisco"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Country *
              </label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="United States"
                required
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Timezone *
              </label>
              <select
                name="timezone"
                value={formData.timezone}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                {TIMEZONES.map(tz => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Contact Email
              </label>
              <input
                type="email"
                name="contact_email"
                value={formData.contact_email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="contact@venue.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Contact Phone
              </label>
              <input
                type="tel"
                name="contact_phone"
                value={formData.contact_phone}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="+1 (555) 123-4567"
              />
            </div>
          </div>

          {/* Map Section */}
          {showMap && formData.address && (
            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  <MapPinIcon className="h-4 w-4 inline mr-1" />
                  Location on Map
                </label>
                <button
                  type="button"
                  onClick={() => setShowMap(false)}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300"
                >
                  Hide Map
                </button>
              </div>
              <div className="border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden">
                <VenueMap
                  address={formData.address}
                  latitude={formData.latitude}
                  longitude={formData.longitude}
                  venueName={formData.name || 'Venue Location'}
                  height="300px"
                  zoom={15}
                  interactive={true}
                  onLocationChange={handleMapLocationChange}
                />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Click on the map to adjust the exact location if needed
              </p>
            </div>
          )}

          {!showMap && formData.address && (
            <div className="md:col-span-2">
              <button
                type="button"
                onClick={() => setShowMap(true)}
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800"
              >
                <MapPinIcon className="h-4 w-4 inline mr-1" />
                Show on Map
              </button>
            </div>
          )}

          <div className="text-sm text-gray-500 dark:text-gray-400 italic">
            * Required fields
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
              disabled={loading || !formData.name || !formData.address || !formData.city || !formData.country}
            >
              {loading ? 'Saving...' : mode === 'create' ? 'Create Venue' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VenueEditModal;